import { Link } from "wouter";
import { useState } from "react";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const platformLinks = [
  { label: "Browse Games", href: "/games" },
  { label: "Create a Game", href: "/create" },
  { label: "Leaderboard", href: "/leaderboard" },
  { label: "For Creators", href: "/creators" },
  { label: "For Promoters", href: "/promoters" },
];

const supportLinks = [
  { label: "Support", href: "/support" },
  { label: "FAQ", href: "/faq" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Acceptable Use Policy", href: "/acceptable-use" },
  { label: "Pricing", href: "/pricing" },
];

export default function PublicFooter() {
  const [email, setEmail] = useState("");
  const subscribe = trpc.newsletter.subscribe.useMutation({
    onSuccess: (data) => {
      toast.success(
        data.alreadySubscribed
          ? "You're already subscribed to insider updates."
          : "Successfully subscribed! Check your email for updates."
      );
      setEmail("");
    },
    onError: (error) => {
      toast.error(error.message || "Subscription failed. Please try again.");
    },
  });

  const handleSubscribe = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();

    if (!trimmed) {
      toast.error("Enter an email address to subscribe.");
      return;
    }

    subscribe.mutate({ email: trimmed });
  };

  return (
    <footer className="relative border-t border-border/30 bg-background overflow-hidden">
      {/* Decorative background glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-neon/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-neon/15 border border-neon/30 flex items-center justify-center">
                <span className="text-neon font-extrabold text-sm tracking-tighter">GP</span>
              </div>
              <span className="text-xl font-extrabold tracking-tight uppercase">
                <span className="text-white">Game</span>
                <span className="text-neon">Point</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              The ultimate platform for competitive skill-based tournaments and prize pools.
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              &copy; {new Date().getFullYear()} GamePoint. All rights reserved.
            </p>
            <div className="mt-4">
              <LanguageSelector />
            </div>
          </div>

          {/* Platform */}
          <div>
            <h4 className="text-sm font-bold text-foreground uppercase tracking-wide mb-4">Platform</h4>
            <ul className="space-y-2.5">
              {platformLinks.map((link) => (
                <li key={link.label}>
                  <Link href={link.href}>
                    <span className="text-sm text-muted-foreground hover:text-neon transition-colors cursor-pointer">
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-sm font-bold text-foreground uppercase tracking-wide mb-4">Support</h4>
            <ul className="space-y-2.5">
              {supportLinks.map((link) => (
                <li key={link.label}>
                  <Link href={link.href}>
                    <span className="text-sm text-muted-foreground hover:text-neon transition-colors cursor-pointer">
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-bold text-foreground uppercase tracking-wide mb-2">
              Be the First to Know
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe for insider info and a first-play advantage.
            </p>
            <form className="flex gap-0" onSubmit={handleSubscribe}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={subscribe.isPending}
                className="flex-1 bg-navy-light border border-border rounded-l-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50"
              />
              <Button
                type="submit"
                className="bg-neon hover:bg-neon-dim text-background rounded-l-none px-3"
                disabled={subscribe.isPending}
                aria-label="Subscribe to newsletter"
              >
                {subscribe.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </Button>
            </form>

            {/* Social icons — links go live as official accounts launch */}
            <div className="flex items-center gap-4 mt-6 text-[11px] text-muted-foreground">
              <span>Facebook</span>
              <span aria-hidden="true">·</span>
              <span>YouTube</span>
              <span aria-hidden="true">·</span>
              <span>X (Twitter)</span>
              <span aria-hidden="true">·</span>
              <span>Discord</span>
              <span className="italic opacity-70">(coming soon)</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
